import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { ymGoal } from '../utils/ym';
import './BuyCrystalsModal.css';

interface BuyCrystalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Фиксированные пакеты кристаллов. Цена авторитетно считается на сервере
// (config payments_sbp.packages); здесь — только для отображения.
const PACKAGES: { crystals: number; price: number }[] = [
  { crystals: 350, price: 420 },
  { crystals: 600, price: 720 },
  { crystals: 1000, price: 1200 },
];

export const BuyCrystalsModal: React.FC<BuyCrystalsModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selected, setSelected] = useState<number>(PACKAGES[0].crystals);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Управление скроллом body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedPackage = PACKAGES.find((p) => p.crystals === selected) ?? PACKAGES[0];

  const handlePurchase = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.createPaymentSBP(selected);

      if (response.success && response.payment_url) {
        // Цель Яндекс.Метрики: инициировано пополнение баланса (Соня 29.07).
        ymGoal('balance-topup');

        // Открываем страницу оплаты через Telegram.openLink
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(response.payment_url);
        } else {
          // Fallback для случая вне Telegram
          window.open(response.payment_url, '_blank');
        }

        onSuccess?.();
        handleClose();
      } else {
        setError(response.message || 'Не удалось создать платеж');
      }
    } catch (err: any) {
      console.error('Payment creation error:', err);
      setError(err.response?.data?.message || 'Произошла ошибка при создании платежа');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelected(PACKAGES[0].crystals);
    setError(null);
    onClose();
  };

  return (
    <div className="buy-crystals-overlay" onClick={handleClose}>
      <div className="buy-crystals-modal" onClick={(e) => e.stopPropagation()}>
        <button className="buy-crystals-close" onClick={handleClose}>
          ✕
        </button>

        <div className="buy-crystals-header">
          <h2 className="buy-crystals-title">🌻 Пополнить баланс</h2>
          <p className="buy-crystals-subtitle">
            Выберите пакет цветов
          </p>
        </div>

        <div className="buy-crystals-content">
          <div className="crystal-packages" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PACKAGES.map((p) => {
              const active = p.crystals === selected;
              return (
                <button
                  key={p.crystals}
                  className={`crystal-package-btn ${active ? 'active' : ''}`}
                  onClick={() => { setSelected(p.crystals); setError(null); }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 16px',
                    borderRadius: 14,
                    // Модалка тёмная (см. BuyCrystalsModal.css), текст белый — поэтому
                    // фон карточек НЕ непрозрачно-белый (иначе белый текст сливается,
                    // видно только эмодзи). Делаем тёмно-полупрозрачным под тему.
                    border: active ? '2px solid #C49A6C' : '2px solid rgba(255,255,255,0.14)',
                    background: active ? 'rgba(196,154,108,0.18)' : 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🌻 {p.crystals.toLocaleString()}</span>
                  <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{p.price.toLocaleString()} ₽</span>
                </button>
              );
            })}
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="price-summary">
            <div className="price-row">
              <span className="price-label">Цветы:</span>
              <span className="price-value">🌻 {selectedPackage.crystals.toLocaleString()}</span>
            </div>
            <div className="price-row total">
              <span className="price-label">К оплате:</span>
              <span className="price-value">{selectedPackage.price.toLocaleString()} ₽</span>
            </div>
          </div>
        </div>

        <button
          className="buy-crystals-submit"
          onClick={handlePurchase}
          disabled={isLoading}
        >
          {isLoading ? 'Создание платежа...' : `Оплатить ${selectedPackage.price.toLocaleString()} ₽`}
        </button>

        <p className="payment-note">
          Оплата через СБП. После нажатия откроется страница оплаты.
        </p>
      </div>
    </div>
  );
};
